import { getWebPath, TableSettingsTabType } from '@doss/common-all'
import { last, nth } from 'lodash'
import { ReactElement, useMemo } from 'react'
import { DossIcon } from '~/components/DossIcon'
import { PageType, RouteCrumb } from '~/router/types'
import {
  BreadcrumbObjectType,
  ChartType,
  GetBreadcrumbsQuery,
  GetWorkflowNameQuery,
  useGetBreadcrumbsQuery,
  useGetSimpleTableQuery,
  useGetSimpleWorkspaceQuery,
  useGetWorkflowNameQuery,
} from '../generated/apollo-operations'
import { TranslationKeys } from '../generated/i18n.generated'
import { useGlobalContext } from '../hooks/useGlobalContext'
import { useOrgProjects } from '../hooks/useOrgProjects'
import { usePage } from '../hooks/usePage'
import { useTypedTranslation } from '../hooks/useTypedTranslation'
import { gray } from '../styles/colors'

export interface Breadcrumb {
  id?: string
  link: string
  name: string
  workspaceType?: BreadcrumbObjectType
  showViewDropdown: boolean
  icon?: ReactElement
}

export const breadcrumbTitle = (t: (key: TranslationKeys) => string, breadcrumbs: Breadcrumb[]) => {
  const lastBreadcrumb = last(breadcrumbs)
  if (!lastBreadcrumb) {
    return t('breadcrumbs.home')
  }
  if (!lastBreadcrumb.workspaceType) {
    return lastBreadcrumb.name
  }
  if (lastBreadcrumb.workspaceType === BreadcrumbObjectType.TABLE_ROW) {
    const parent = nth(breadcrumbs, -2)
    return parent ? `${parent.name} / ${lastBreadcrumb.name}` : lastBreadcrumb.name
  }
  if (lastBreadcrumb.workspaceType === BreadcrumbObjectType.ORGANIZATION) {
    return t('breadcrumbs.home')
  }
  return lastBreadcrumb.name
}

export const ObjectsWithoutWorkspace = new Set([
  BreadcrumbObjectType.ACTION,
  BreadcrumbObjectType.ACTION_STEP,
  BreadcrumbObjectType.FORM,
  BreadcrumbObjectType.FORM_PAGE,
  BreadcrumbObjectType.FORM_CATEGORY,
  BreadcrumbObjectType.FORM_FIELD,
  BreadcrumbObjectType.ORGANIZATION,
  BreadcrumbObjectType.TABLE_COLUMN,
  BreadcrumbObjectType.TABLE_CELL,
  BreadcrumbObjectType.USER,
  BreadcrumbObjectType.WORKFLOW,
])

// ── Helpers ─────────────────────────────────────────────────────────────

type ApiBreadcrumb = GetBreadcrumbsQuery['breadcrumbs'][number]

const translateCrumbs = (crumbs: RouteCrumb[], t: (key: TranslationKeys) => string): Breadcrumb[] =>
  crumbs.map((crumb) => ({
    link: crumb.pathname,
    name: t(crumb.label as TranslationKeys),
    showViewDropdown: false,
  }))

const toEntityBreadcrumb = (
  apiBreadcrumb: ApiBreadcrumb,
  index: number,
  total: number,
  suppressDropdown: boolean,
  hasParentTable: boolean,
): Breadcrumb => {
  const webPathInput = { objectType: apiBreadcrumb.type, objectId: apiBreadcrumb.objectId }
  return {
    id: apiBreadcrumb.objectId,
    link: getWebPath(
      hasParentTable
        ? { ...webPathInput, settings: true, settingsParams: { tab: TableSettingsTabType.FIELDS } }
        : webPathInput,
    ),
    workspaceType: apiBreadcrumb.type,
    name: apiBreadcrumb.name,
    showViewDropdown: !suppressDropdown && index === total - 1,
    icon: apiBreadcrumb.iconName ? (
      <DossIcon color={gray[6]} name={apiBreadcrumb.iconName} size={12} />
    ) : undefined,
  }
}

const buildWorkflowCrumbs = (
  pageType: PageType | undefined,
  apiBreadcrumbs: ApiBreadcrumb[],
  workflow: GetWorkflowNameQuery['workflow'] | undefined,
  t: (key: TranslationKeys) => string,
): Breadcrumb[] => {
  if (pageType !== 'workflow') {
    return []
  }
  const lastApiBreadCrumb = last(apiBreadcrumbs)
  if (!lastApiBreadCrumb) {
    return []
  }
  const webPathInput = {
    objectType: lastApiBreadCrumb.type,
    objectId: lastApiBreadCrumb.objectId,
    workflow: {},
  }
  return [
    {
      link: getWebPath(webPathInput),
      name: t('breadcrumbs.workflows'),
      workspaceType: BreadcrumbObjectType.WORKFLOW,
      showViewDropdown: false,
    },
    ...(workflow
      ? [
          {
            link: getWebPath(webPathInput),
            name: workflow.name,
            workspaceType: BreadcrumbObjectType.WORKFLOW,
            showViewDropdown: false,
          },
        ]
      : []),
  ]
}

/** Resolves a workspace to its underlying entity for breadcrumb queries. */
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const resolveWorkspaceEntity = (workspace: any) => {
  const ws = workspace?.workspace
  if (!ws) {
    return { objectId: undefined as string | undefined, objectType: BreadcrumbObjectType.WORKSPACE }
  }
  // A root workspace is the PROJECT entity, identified by its own id
  if (ws.parentOrganization) {
    return { objectId: ws.id as string, objectType: BreadcrumbObjectType.PROJECT }
  }
  if (ws.document) {
    return { objectId: ws.document.id as string, objectType: BreadcrumbObjectType.DOCUMENT }
  }
  if (ws.table) {
    return { objectId: ws.table.id as string, objectType: BreadcrumbObjectType.TABLE }
  }
  if (ws.tableRow) {
    return { objectId: ws.tableRow.id as string, objectType: BreadcrumbObjectType.TABLE_ROW }
  }
  if (ws.chart?.chartType === ChartType.PIVOT_TABLE) {
    return { objectId: ws.chart.id as string, objectType: BreadcrumbObjectType.PIVOT_TABLE }
  }
  if (ws.chart) {
    return { objectId: ws.chart.id as string, objectType: BreadcrumbObjectType.CHART }
  }
  return { objectId: ws.id as string | undefined, objectType: BreadcrumbObjectType.WORKSPACE }
}

// ── Main hook ───────────────────────────────────────────────────────────

/**
 * Self-contained hook that builds the breadcrumb trail from routing + API data.
 * Reads group/crumbs/pageType from usePage() (route hierarchy), fetches entity
 * names from the API, and composes the result declaratively.
 */
export const useBuildBreadcrumbs = (dynamicFinalBreadcrumbName?: string) => {
  const { group, crumbs, pageType, objectId, objectType, query } = usePage()
  const { user } = useGlobalContext()
  const { t } = useTypedTranslation()
  const { selectedProject, projects } = useOrgProjects()
  const { workspaceId, table_id, workflow_id } = query

  // Workspace → entity resolution
  const { data: workspace } = useGetSimpleWorkspaceQuery({
    variables: { workspaceId: workspaceId as string },
    skip: typeof workspaceId !== 'string',
  })
  const resolved = useMemo(() => resolveWorkspaceEntity(workspace), [workspace])

  // Parent table check (for child table settings link override)
  const { data: table } = useGetSimpleTableQuery({
    variables: { tableId: table_id as string },
    skip: typeof table_id !== 'string',
  })
  const hasParentTable = !!table?.table.parentTable

  // Breadcrumb query variables
  const breadcrumbInput = useMemo(() => {
    if (resolved.objectId) {
      return { objectId: resolved.objectId, objectType: resolved.objectType }
    }
    return { objectId: objectId ?? '', objectType: objectType ?? BreadcrumbObjectType.ORGANIZATION }
  }, [objectId, objectType, resolved])

  // Entity breadcrumbs from API
  const skipQuery =
    (!resolved.objectId && (!objectId || !objectType)) ||
    !user ||
    pageType === 'workflow' ||
    group === 'settings'
  const { data, loading } = useGetBreadcrumbsQuery({
    variables: { input: breadcrumbInput },
    skip: skipQuery,
  })

  const { data: workflowData } = useGetWorkflowNameQuery({
    variables: { workflowId: workflow_id as string },
    skip: typeof workflow_id !== 'string',
  })

  // Build breadcrumbs
  if (!user) {
    return { breadcrumbs: [] as Breadcrumb[], loading: false }
  }

  const rootName = selectedProject?.name || projects.at(0)?.name || user.organizationName
  const rootCrumb: Breadcrumb = {
    link: '/',
    name: rootName,
    workspaceType: BreadcrumbObjectType.PROJECT,
    showViewDropdown: false,
  }

  if (group === 'settings') {
    return { breadcrumbs: [rootCrumb, ...translateCrumbs(crumbs, t)], loading: false }
  }

  const apiBreadcrumbs = data?.breadcrumbs ?? []
  const suppressDropdown =
    crumbs.length > 0 || pageType === 'workflow' || pageType === 'createTable' || hasParentTable

  const entityCrumbs =
    apiBreadcrumbs.length > 0
      ? apiBreadcrumbs.map((bc, i) =>
          toEntityBreadcrumb(bc, i, apiBreadcrumbs.length, suppressDropdown, hasParentTable),
        )
      : [rootCrumb]

  const breadcrumbs = [
    ...entityCrumbs,
    ...buildWorkflowCrumbs(pageType, apiBreadcrumbs, workflowData?.workflow, t),
    ...(pageType === 'createTable'
      ? [
          {
            link: '/',
            workspaceType: BreadcrumbObjectType.TABLE,
            name: dynamicFinalBreadcrumbName ?? '',
            showViewDropdown: true,
          },
        ]
      : []),
    ...translateCrumbs(crumbs, t),
  ]

  return { breadcrumbs, loading }
}
